import React, { useState, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../firebase/firebase";
import { useLanguage } from "../../../../context/LanguageContext";
import { useAdminSettingsTranslation } from "../translations";

const EmailConfigTab = ({ storeId }) => {
    const { language } = useLanguage();
    const { t } = useAdminSettingsTranslation(language);

    const [emailConfig, setEmailConfig] = useState({
        emailFrom: "",
        emailPassword: "",
        emailHost: "smtp.hostinger.com",
        emailPort: 587,
        emailSecure: false,
        isConfigured: false,
        source: "environment"
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [testEmail, setTestEmail] = useState("");

    useEffect(() => {
        fetchEmailConfig();
    }, [storeId]);

    const fetchEmailConfig = async () => {
        setLoading(true);
        setError("");

        try {
            const getEmailConfiguration = httpsCallable(functions, "getEmailConfiguration");
            const result = await getEmailConfiguration({ storeId });

            if (result.data.success) {
                setEmailConfig(result.data.emailConfig);
            }
        } catch (err) {
            console.error("Error fetching email config:", err);
            setError(t("failedToLoadEmailConfig") || "Failed to load email configuration");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setEmailConfig({
            ...emailConfig,
            [name]: type === "checkbox" ? checked : (name === "emailPort" ? parseInt(value) : value)
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (!emailConfig.emailFrom || !emailConfig.emailPassword) {
            setError(t("emailFromAndPasswordRequired") || "Email and password are required");
            return;
        }

        setSaving(true);
        setError("");
        setSuccess("");

        try {
            const updateEmailConfiguration = httpsCallable(functions, "updateEmailConfiguration");
            const result = await updateEmailConfiguration({
                storeId,
                emailConfig: {
                    emailFrom: emailConfig.emailFrom,
                    emailPassword: emailConfig.emailPassword,
                    emailHost: emailConfig.emailHost,
                    emailPort: emailConfig.emailPort,
                    emailSecure: emailConfig.emailSecure
                }
            });

            if (result.data.success) {
                setSuccess(t("emailConfigSaved") || "Email configuration saved successfully!");
                await fetchEmailConfig(); // Refresh to get updated data
            }
        } catch (err) {
            console.error("Error saving email config:", err);
            setError(err.message || (t("failedToSaveEmailConfig") || "Failed to save email configuration"));
        } finally {
            setSaving(false);
        }
    };

    const handleTestEmail = async () => {
        if (!testEmail && !emailConfig.emailFrom) {
            setError(t("enterTestEmail") || "Please enter a test email address");
            return;
        }

        setTesting(true);
        setError("");
        setSuccess("");

        try {
            const testEmailConfiguration = httpsCallable(functions, "testEmailConfiguration");
            const result = await testEmailConfiguration({
                storeId,
                testEmail: testEmail || undefined
            });

            if (result.data.success) {
                setSuccess(result.data.message || (t("testEmailSent") || "Test email sent successfully!"));
            }
        } catch (err) {
            console.error("Error testing email config:", err);
            setError(err.message || (t("failedToSendTestEmail") || "Failed to send test email"));
        } finally {
            setTesting(false);
        }
    };

    if (loading) {
        return (
            <div className="email-config-loading">
                <div className="loader"></div>
                <p>{t("loading") || "Loading..."}</p>
            </div>
        );
    }

    return (
        <div className="email-config-tab">
            <h2>{t("emailConfiguration") || "Email Configuration"}</h2>
            <p className="email-config-description">
                {t("emailConfigDescription") || "Configure SMTP settings for sending order confirmations, password resets, and notifications."}
            </p>

            {/* Status Badge */}
            <div className={`email-config-status ${emailConfig.isConfigured ? 'configured' : 'not-configured'}`}>
                {emailConfig.isConfigured ? (
                    <>
                        <span className="status-icon">✅</span>
                        <span>{t("configured") || "Configured"}</span>

                    </>
                ) : (
                    <>
                        <span className="status-icon">⚠️</span>
                        <span>{t("usingDefaultConfig") || "Using default email; noreply@raffak.store"}</span>
                    </>
                )}
            </div>

            {error && <div className="admin-error-message">{error}</div>}
            {success && <div className="admin-success-message">{success}</div>}

            <form onSubmit={handleSave} className="email-config-form">
                {/* Email From (Sender) */}
                <div className="form-group">
                    <label htmlFor="emailFrom">
                        {t("emailFrom") || "Email Address (From)"}
                        <span className="required">*</span>
                    </label>
                    <input
                        type="email"
                        id="emailFrom"
                        name="emailFrom"
                        value={emailConfig.emailFrom}
                        onChange={handleChange}
                        placeholder="noreply@yourstore.com"
                        required
                    />
                    <small className="form-help">
                        {t("emailFromHelp") || "This email address will appear as the sender for all store emails"}
                    </small>
                </div>

                {/* Email Password */}
                <div className="form-group">
                    <label htmlFor="emailPassword">
                        {t("emailPassword") || "Email Password / App Password"}
                        <span className="required">*</span>
                    </label>
                    <input
                        type="password"
                        id="emailPassword"
                        name="emailPassword"
                        value={emailConfig.emailPassword}
                        onChange={handleChange}
                        placeholder={emailConfig.hasPassword ? "••••••••" : "Enter password"}
                        required
                    />
                    <small className="form-help">
                        {t("emailPasswordHelp") || "Your email account password or app-specific password"}
                    </small>
                </div>

                {/* SMTP Host */}
                <div className="form-group">
                    <label htmlFor="emailHost">
                        {t("smtpHost") || "SMTP Host"}
                    </label>
                    <input
                        type="text"
                        id="emailHost"
                        name="emailHost"
                        value={emailConfig.emailHost}
                        onChange={handleChange}
                        placeholder="smtp.hostinger.com"
                    />
                    <small className="form-help">
                        {t("smtpHostHelp") || "Common: smtp.hostinger.com, smtp.gmail.com, smtp.office365.com"}
                    </small>
                </div>

                {/* SMTP Port */}
                <div className="form-group">
                    <label htmlFor="emailPort">
                        {t("smtpPort") || "SMTP Port"}
                    </label>
                    <input
                        type="number"
                        id="emailPort"
                        name="emailPort"
                        value={emailConfig.emailPort}
                        onChange={handleChange}
                        placeholder="587"
                    />
                    <small className="form-help">
                        {t("smtpPortHelp") || "Common: 587 (TLS), 465 (SSL), 25 (unsecured)"}
                    </small>
                </div>

                {/* Use SSL/TLS */}
                <div className="form-group checkbox-group">
                    <label htmlFor="emailSecure">
                        <input
                            type="checkbox"
                            id="emailSecure"
                            name="emailSecure"
                            checked={emailConfig.emailSecure}
                            onChange={handleChange}
                        />
                        {t("useSSL") || "Use SSL/TLS (Port 465)"}
                    </label>
                    <small className="form-help">
                        {t("useSSLHelp") || "Enable this if using port 465. Disable for port 587 (STARTTLS)"}
                    </small>
                </div>

                {/* Save Button */}
                <div className="form-actions">
                    <button
                        type="submit"
                        className="admin-primary-btn"
                        disabled={saving}
                    >
                        {saving ? (t("saving") || "Saving...") : (t("saveEmailConfig") || "Save Email Configuration")}
                    </button>
                </div>
            </form>

            {/* Test Email Section */}
            <div className="email-test-section">
                <h3>{t("testEmailConfig") || "Test Email Configuration"}</h3>
                <p className="email-config-description">
                    {t("testEmailDescription") || "Send a test email to verify your configuration is working correctly"}
                </p>

                <div className="test-email-form">
                    <div className="form-group">
                        <label htmlFor="testEmail">
                            {t("testEmailAddress") || "Test Email Address"}
                        </label>
                        <input
                            type="email"
                            id="testEmail"
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                            placeholder={t("enterTestEmailPlaceholder") || "Leave empty to send to your account email"}
                        />
                    </div>

                    <button
                        type="button"
                        className="admin-secondary-btn"
                        onClick={handleTestEmail}
                        disabled={testing}
                    >
                        {testing ? (t("sendingTest") || "Sending...") : (t("sendTestEmail") || "Send Test Email")}
                    </button>
                </div>
            </div>

            {/* SMTP Provider Examples */}
            <div className="email-config-examples">
                <h3>{t("commonProviders") || "Common SMTP Providers"}</h3>
                <div className="provider-examples">
                    <div className="provider-card">
                        <h4>Hostinger</h4>
                        <p><strong>Host:</strong> smtp.hostinger.com</p>
                        <p><strong>Port:</strong> 587 (TLS) or 465 (SSL)</p>
                        <p><strong>Secure:</strong> Off for 587, On for 465</p>
                    </div>
                    <div className="provider-card">
                        <h4>Gmail</h4>
                        <p><strong>Host:</strong> smtp.gmail.com</p>
                        <p><strong>Port:</strong> 587 (TLS) or 465 (SSL)</p>
                        <p><strong>Note:</strong> Use App Password, not regular password</p>
                    </div>
                    <div className="provider-card">
                        <h4>Microsoft 365</h4>
                        <p><strong>Host:</strong> smtp.office365.com</p>
                        <p><strong>Port:</strong> 587 (TLS)</p>
                        <p><strong>Secure:</strong> Off</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailConfigTab;
